import * as React from 'react';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import useDebounce from '../utils/useDebounce';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getAllOrgUnitsFromGoogleQuery,
  getAllResourceCalendars,
  getGroups,
  getPageOfSharedDrivesForDomain,
  getSharedDrivesByIdsQuery,
  getUserList,
  UserKeys,
} from '../api/users-api';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Group,
  Page,
  SharedDriveModel,
  UserListEntity,
  UserRole,
} from '../../types';
import { useInView } from 'react-intersection-observer';
import SharedBigQueryTransferList from './shared-bigquery-transfer-list';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import { Messages } from '../utils/message-constants';
import { SharedTransferListLayout } from './shared-transfer-list-layout';
import SharedTransferListNew from './shared-transfer-list-new';

import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItem from '@mui/material/ListItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface SharedTransferListProps {
  newUi?: boolean;
  sources?: string[];
  addSelection: (email: string[]) => void;
  removeSelection: (email: string[]) => void;
  handleAddAllChecked?: (value: boolean) => void;
  errors?: boolean;
  allSource?: boolean;
  domains: string[];
  sourceType?: string;
  helperText?: string;
  hasSelectAllOption?: boolean;
  roles?: UserRole[];
  maxSelection?: number;
  setSelectedSharedDrives?: Dispatch<SetStateAction<SharedDriveModel[]>>;
}

function not(a: readonly string[], b: readonly string[]) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a: readonly string[], b: readonly string[]) {
  return a.filter((value) => b.includes(value));
}

function union(a: readonly string[], b: readonly string[]) {
  return [...a, ...not(b, a)];
}

export default function SharedTransferList({
  sources,
  addSelection,
  removeSelection,
  handleAddAllChecked,
  errors,
  allSource,
  domains,
  sourceType,
  helperText,
  hasSelectAllOption,
  roles,
  maxSelection,
  setSelectedSharedDrives,
  newUi = false,
}: SharedTransferListProps) {
  const { currentTier, features } = useUserSettings();
  const [checked, setChecked] = useState<readonly string[]>([]);

  const [left, setLeft] = useState<readonly string[]>([]);
  const [right, setRight] = useState<readonly string[]>(sources || []);

  const [ref, inView] = useInView();
  const [groupRef, groupInView] = useInView();
  const [sharedDriveRef, sharedDriveInView] = useInView();

  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [orgUnitSearch, setOrgUnitSearch] = useState('');
  const [domainSearch, setDomainSearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [calendarSearch, setCalendarSearch] = useState('');
  const [sharedDriveSearch, setSharedDriveSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');

  const debouncedUserSearch = useDebounce(userSearch, 500);
  const debouncedGroupSearch = useDebounce(groupSearch, 500);
  const debouncedOrgUnitSearch = useDebounce(orgUnitSearch, 500);
  const debouncedCalendarSearch = useDebounce(calendarSearch, 500);
  const debouncedSharedDriveSearch = useDebounce(sharedDriveSearch, 500);

  // Use SharedBigQuery components for all enterprise users.
  const enterpriseBackendIndexingEnabled = currentTier === 'enterprise';
  const useBigQueryForUsers = isFeatureEnabled('indexUsersReact', features);
  const useBigQueryForGroups = isFeatureEnabled('indexGroupsReact', features);

  const [paginationModel] = useState<{
    pageSize: number;
    cursor: string | null;
  }>({
    pageSize: 25,
    cursor: null,
  });

  const {
    data: userData,
    fetchNextPage: userDataFetchNextPage,
    isFetchingNextPage: userDataIsFetchingNextPage,
    hasNextPage: userDataHasNextPage,
    isLoading: userDataIsLoading,
  } = useInfiniteQuery({
    queryKey: [UserKeys.UserList, paginationModel, debouncedUserSearch],
    queryFn: ({ pageParam }) => {
      const object: Page<UserListEntity[]> = {
        results: [],
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: pageParam,
        nameSpace: '',
      };
      return getUserList(object, debouncedUserSearch, 'All', null, null);
    },
    initialPageParam: paginationModel.cursor,
    getNextPageParam: (lastPage) => lastPage.cursor,
    meta: {
      errorMessage:
        'There was an issue loading users. Please refresh and try again.',
    },
  });

  const {
    data: groupData,
    fetchNextPage: groupDataFetchNextPage,
    isFetchingNextPage: groupDataIsFetchingNextPage,
    hasNextPage: groupDataHasNextPage,
    isLoading: groupDataIsLoading,
  } = useInfiniteQuery({
    queryKey: [UserKeys.Groups, debouncedGroupSearch],
    queryFn: ({ pageParam }) => {
      const object: Page<Group[]> = {
        results: [],
        searchTime: 0,
        pageSize: 25,
        cursor: pageParam,
        nameSpace: '',
      };
      return getGroups(object, debouncedGroupSearch);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    meta: {
      errorMessage:
        'There was an issue loading groups. Please refresh and try again.',
    },
  });

  const {
    data: sharedDriveData,
    fetchNextPage: sharedDriveDataFetchNextPage,
    isFetchingNextPage: sharedDriveDataIsFetchingNextPage,
    hasNextPage: sharedDriveDataHasNextPage,
    isLoading: sharedDriveDataIsLoading,
  } = useInfiniteQuery({
    queryKey: [UserKeys.SharedDrivesForDomainPage, debouncedSharedDriveSearch],
    queryFn: ({ pageParam }) => {
      const object: Page<SharedDriveModel[]> = {
        results: [],
        searchTime: 0,
        pageSize: 25,
        cursor: pageParam,
        nameSpace: '',
      };
      return getPageOfSharedDrivesForDomain(
        object,
        debouncedSharedDriveSearch,
        ''
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('shared drives'),
    },
    enabled: sourceType === 'Shared_Drive',
  });

  const { data: orgUnitData, isLoading: orgUnitDataIsLoading } = useQuery(
    getAllOrgUnitsFromGoogleQuery()
  );

  useEffect(() => {
    if (inView && userDataHasNextPage && !userDataIsFetchingNextPage) {
      userDataFetchNextPage();
    }

    if (groupInView && groupDataHasNextPage && !groupDataIsFetchingNextPage) {
      groupDataFetchNextPage();
    }

    if (
      sharedDriveInView &&
      sharedDriveDataHasNextPage &&
      !sharedDriveDataIsFetchingNextPage
    ) {
      sharedDriveDataFetchNextPage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, groupInView, sharedDriveInView]);

  const { data: resourceCalendars, isLoading: calendarsIsLoading } = useQuery({
    enabled: sourceType === 'Resource_Calendar',
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
  });

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) {
      return new Map<string, string>();
    }
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  const { data: initialSharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(
      sources && sourceType === 'Shared_Drive' ? sources : []
    ),
    enabled: !!(sources && sources.length > 0 && sourceType === 'Shared_Drive'),
    staleTime: Infinity,
  });

  const sharedDriveIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (sharedDriveData) {
      const allSharedDrives = sharedDriveData.pages.flatMap(
        (page) => page.results
      );
      allSharedDrives.forEach((d) => map.set(d.id, d.name));
    }

    if (initialSharedDrives) {
      initialSharedDrives.forEach((d) => map.set(d.id, d.name));
    }

    return map;
  }, [sharedDriveData, initialSharedDrives]);

  const memoizedRight = useMemo(() => right, [right]);

  // Updates selectedSharedDrives for the review step based on the current selection, appending to the previous value.
  useEffect(() => {
    if (!setSelectedSharedDrives || sourceType !== 'Shared_Drive') return;

    const allKnownDrives = [
      ...(initialSharedDrives || []),
      ...(sharedDriveData?.pages.flatMap((page) => page.results) || []),
    ];
    const driveMap = new Map(allKnownDrives.map((d) => [d.id, d]));

    const currentSelection = right
      .map((id) => driveMap.get(id))
      .filter((drive): drive is SharedDriveModel => !!drive);

    setSelectedSharedDrives(currentSelection);
  }, [
    right,
    sharedDriveData,
    initialSharedDrives,
    setSelectedSharedDrives,
    sourceType,
  ]);

  const leftFiltered = useMemo(() => {
    const searchValue = ((): string => {
      if (sourceType === 'User') return debouncedUserSearch;
      if (sourceType === 'Group') return debouncedGroupSearch;
      if (sourceType === 'OrgUnit') return debouncedOrgUnitSearch;
      if (sourceType === 'Domain') return domainSearch;
      if (sourceType === 'gPanel_Role') return roleSearch;
      if (sourceType === 'Resource_Calendar') return debouncedCalendarSearch;
      if (sourceType === 'Shared_Drive') return debouncedSharedDriveSearch;
      return '';
    })().toLowerCase();

    if (!searchValue) {
      return left;
    }

    if (sourceType === 'Resource_Calendar') {
      return left.filter((item) => {
        const name = calendarEmailToNameMap.get(item) || '';
        return name.toLowerCase().includes(searchValue);
      });
    } else if (sourceType === 'Shared_Drive') {
      return left.filter((item) => {
        const name = sharedDriveIdToNameMap.get(item) || '';
        return name.toLowerCase().includes(searchValue);
      });
    }
    return left.filter((item) => item.toLowerCase().includes(searchValue));
  }, [
    left,
    debouncedUserSearch,
    debouncedGroupSearch,
    debouncedOrgUnitSearch,
    domainSearch,
    debouncedCalendarSearch,
    debouncedSharedDriveSearch,
    roleSearch,
    sourceType,
    calendarEmailToNameMap,
    sharedDriveIdToNameMap,
  ]);

  const rightFiltered = useMemo(() => {
    const searchValue = rightSearch.toLowerCase();

    if (!searchValue) {
      return right;
    }

    if (sourceType === 'Resource_Calendar') {
      return right.filter((item) => {
        const name = calendarEmailToNameMap.get(item) || '';
        return name.toLowerCase().includes(searchValue);
      });
    } else if (sourceType === 'Shared_Drive') {
      return right.filter((item) => {
        const name = sharedDriveIdToNameMap.get(item) || '';
        return name.toLowerCase().includes(searchValue);
      });
    }

    return right.filter((item) => item.toLowerCase().includes(searchValue));
  }, [
    right,
    rightSearch,
    sourceType,
    calendarEmailToNameMap,
    sharedDriveIdToNameMap,
  ]);

  const filteredData = useMemo(() => {
    if (sourceType === 'User') {
      const allUsers = userData?.pages.flatMap((page) => page.results) || [];

      return not(
        allUsers.map((user) => user.userEmail),
        memoizedRight
      );
    } else if (sourceType === 'Group') {
      const allGroups = groupData?.pages.flatMap((page) => page.results) || [];
      return not(
        allGroups.map((group) => group.email),
        memoizedRight
      );
    } else if (sourceType === 'OrgUnit') {
      const allOrgUnits = orgUnitData || [];
      return not(
        allOrgUnits.map((orgUnit) => orgUnit.orgUnitPath),
        memoizedRight
      );
    } else if (sourceType === 'Domain') {
      const allDomains = domains?.map((domain) => domain) || [];
      return not(
        allDomains.map((domain) => domain),
        memoizedRight
      );
    } else if (sourceType === 'gPanel_Role') {
      const allRoles = roles?.map((role) => role.roleName) || [];
      return not(
        allRoles.map((role) => role),
        memoizedRight
      );
    } else if (sourceType === 'Resource_Calendar') {
      const allCalendars = resourceCalendars || [];
      return not(
        allCalendars.map((calendar) => calendar.resourceEmail),
        memoizedRight
      );
    } else if (sourceType === 'Shared_Drive') {
      const allSharedDrives =
        sharedDriveData?.pages.flatMap((page) => page.results) || [];
      return not(
        allSharedDrives.map((drive) => drive.id),
        memoizedRight
      );
    }
    return [];
  }, [
    userData,
    groupData,
    orgUnitData,
    sharedDriveData,
    memoizedRight,
    sourceType,
    domains,
    roles,
    resourceCalendars,
  ]);

  useEffect(() => {
    if (sourceType === 'User') {
      setLeft(filteredData);
    } else if (sourceType === 'Group') {
      setLeft(filteredData);
    } else if (sourceType === 'OrgUnit') {
      setLeft(filteredData);
    } else if (sourceType === 'Domain') {
      setLeft(filteredData);
    } else if (sourceType === 'gPanel_Role') {
      setLeft(filteredData);
    } else if (sourceType === 'Resource_Calendar') {
      setLeft(filteredData);
    } else if (sourceType === 'Shared_Drive') {
      setLeft(filteredData);
    }
  }, [filteredData, sourceType]);

  // Preserve selected items when search changes
  useEffect(() => {
    const currentSearchValue = ((): string => {
      if (sourceType === 'User') return debouncedUserSearch;
      if (sourceType === 'Group') return debouncedGroupSearch;
      if (sourceType === 'OrgUnit') return debouncedOrgUnitSearch;
      if (sourceType === 'Domain') return domainSearch;
      if (sourceType === 'gPanel_Role') return roleSearch;
      if (sourceType === 'Resource_Calendar') return debouncedCalendarSearch;
      if (sourceType === 'Shared_Drive') return debouncedSharedDriveSearch;
      return '';
    })();

    // If search is cleared, restore the full list while preserving selections
    if (!currentSearchValue && left.length > 0) {
      const allAvailableItems = (() => {
        if (sourceType === 'User') {
          const allUsers =
            userData?.pages.flatMap((page) => page.results) || [];
          return allUsers.map((user) => user.userEmail);
        } else if (sourceType === 'Group') {
          const allGroups =
            groupData?.pages.flatMap((page) => page.results) || [];
          return allGroups.map((group) => group.email);
        } else if (sourceType === 'OrgUnit') {
          const allOrgUnits = orgUnitData || [];
          return allOrgUnits.map((orgUnit) => orgUnit.orgUnitPath);
        } else if (sourceType === 'Domain') {
          return domains || [];
        } else if (sourceType === 'gPanel_Role') {
          return roles?.map((role) => role.roleName) || [];
        } else if (sourceType === 'Resource_Calendar') {
          const allCalendars = resourceCalendars || [];
          return allCalendars.map((calendar) => calendar.resourceEmail);
        } else if (sourceType === 'Shared_Drive') {
          const allSharedDrives =
            sharedDriveData?.pages.flatMap((page) => page.results) || [];
          return allSharedDrives.map((drive) => drive.id);
        }
        return [];
      })();

      const newLeft = not(allAvailableItems, memoizedRight);

      // Only update if the new left list is different from current
      if (
        JSON.stringify([...newLeft].sort()) !== JSON.stringify([...left].sort())
      ) {
        setLeft(newLeft);
      }
    }
  }, [
    debouncedUserSearch,
    debouncedGroupSearch,
    debouncedOrgUnitSearch,
    debouncedSharedDriveSearch,
    domainSearch,
    roleSearch,
    debouncedCalendarSearch,
    sourceType,
    left,
    checked,
    memoizedRight,
    userData,
    groupData,
    orgUnitData,
    sharedDriveData,
    domains,
    roles,
    resourceCalendars,
  ]);

  const handleSearchInput = (value: string, isLeftList: boolean) => {
    if (isLeftList) {
      if (sourceType === 'User') {
        setUserSearch(value);
      } else if (sourceType === 'Group') {
        setGroupSearch(value);
      } else if (sourceType === 'OrgUnit') {
        setOrgUnitSearch(value);
      } else if (sourceType === 'Domain') {
        setDomainSearch(value);
      } else if (sourceType === 'gPanel_Role') {
        setRoleSearch(value);
      } else if (sourceType === 'Resource_Calendar') {
        setCalendarSearch(value);
      } else if (sourceType === 'Shared_Drive') {
        setSharedDriveSearch(value);
      }
    } else {
      setRightSearch(value);
    }
  };

  const currentSearchValue = useMemo(() => {
    if (sourceType === 'User') return userSearch;
    if (sourceType === 'Group') return groupSearch;
    if (sourceType === 'OrgUnit') return orgUnitSearch;
    if (sourceType === 'Domain') return domainSearch;
    if (sourceType === 'gPanel_Role') return roleSearch;
    if (sourceType === 'Resource_Calendar') return calendarSearch;
    if (sourceType === 'Shared_Drive') return sharedDriveSearch;
    return '';
  }, [
    userSearch,
    groupSearch,
    orgUnitSearch,
    domainSearch,
    sourceType,
    roleSearch,
    calendarSearch,
    sharedDriveSearch,
  ]);

  const handleToggle = useCallback(
    (value: string) => () => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
    },
    [checked]
  );

  const numberOfChecked = useCallback(
    (items: readonly string[]) => intersection(checked, items).length,
    [checked]
  );

  const getAllAvailableItems = useCallback(() => {
    if (sourceType === 'User') {
      const allUsers = userData?.pages.flatMap((page) => page.results) || [];
      return allUsers.map((user) => user.userEmail);
    } else if (sourceType === 'Group') {
      const allGroups = groupData?.pages.flatMap((page) => page.results) || [];
      return allGroups.map((group) => group.email);
    } else if (sourceType === 'OrgUnit') {
      const allOrgUnits = orgUnitData || [];
      return allOrgUnits.map((orgUnit) => orgUnit.orgUnitPath);
    } else if (sourceType === 'Domain') {
      return domains || [];
    } else if (sourceType === 'gPanel_Role') {
      return roles?.map((role) => role.roleName) || [];
    } else if (sourceType === 'Resource_Calendar') {
      const allCalendars = resourceCalendars || [];
      return allCalendars.map((calendar) => calendar.resourceEmail);
    } else if (sourceType === 'Shared_Drive') {
      const allSharedDrives =
        sharedDriveData?.pages.flatMap((page) => page.results) || [];
      return allSharedDrives.map((drive) => drive.id);
    }
    return [];
  }, [
    sourceType,
    userData,
    groupData,
    orgUnitData,
    sharedDriveData,
    domains,
    roles,
    resourceCalendars,
  ]);

  const leftChecked = not(checked, right);
  const rightChecked = intersection(checked, right);

  const isMaxSelection = !!(
    maxSelection &&
    sources &&
    leftChecked.length + sources.length > maxSelection
  );

  const getSelectedCountForSourceType = useCallback(() => {
    return not(checked, right).length;
  }, [checked, right]);

  const handleToggleAll = useCallback(
    (items: readonly string[]) => () => {
      if (numberOfChecked(items) === items.length) {
        setChecked(not(checked, items));
      } else {
        setChecked(union(checked, items));
      }
    },
    [checked, numberOfChecked]
  );

  const handleToggleAllLeft = useCallback(() => {
    const allAvailableItems = getAllAvailableItems();
    const currentlySelected = intersection(checked, allAvailableItems);

    if (currentlySelected.length === allAvailableItems.length) {
      setChecked(not(checked, allAvailableItems));
    } else {
      setChecked(union(checked, allAvailableItems));
    }
  }, [checked, getAllAvailableItems]);

  const handleToggleAllRight = useCallback(() => {
    const currentlySelected = intersection(checked, rightFiltered);

    if (currentlySelected.length === rightFiltered.length) {
      setChecked(not(checked, rightFiltered));
    } else {
      setChecked(union(checked, rightFiltered));
    }
  }, [checked, rightFiltered]);

  const handleCheckedRight = () => {
    const selectedEmails = leftChecked;

    if (selectedEmails.length === 0) return;

    const newRight = Array.from(new Set([...right, ...selectedEmails]));
    setRight(newRight);

    const newLeft = not(getAllAvailableItems(), newRight);
    setLeft(newLeft);

    setChecked(not(checked, selectedEmails));

    addSelection(selectedEmails as string[]);
  };

  const handleCheckedLeft = () => {
    const selectedEmails = rightChecked;

    if (selectedEmails.length === 0) return;

    const newRight = not(right, rightChecked);
    setRight(newRight);

    const newLeft = not(getAllAvailableItems(), newRight);
    setLeft(newLeft);

    setChecked(not(checked, rightChecked));

    removeSelection(selectedEmails);
  };

  useEffect(() => {
    // Clear all checked items when source type changes
    setChecked([]);

    // Reset searches tied to sourceType
    setUserSearch('');
    setGroupSearch('');
    setOrgUnitSearch('');
    setDomainSearch('');
    setRoleSearch('');
    setCalendarSearch('');
    setSharedDriveSearch('');
    setRightSearch('');
  }, [sourceType]);

  const showIncludeAll =
    !!hasSelectAllOption &&
    ['User', 'Group', 'All_Users', 'All_Groups', 'Resource_Calendar'].includes(
      sourceType ?? ''
    );

  const leftLoading =
    userDataIsLoading ||
    groupDataIsLoading ||
    orgUnitDataIsLoading ||
    calendarsIsLoading ||
    sharedDriveDataIsLoading;

  const handleIncludeAllChange = useCallback(
    (value: boolean) => {
      setRight([]);
      if (handleAddAllChecked) {
        handleAddAllChecked(value);
      }
    },
    [handleAddAllChecked]
  );

  const getItemLabel = useCallback(
    (value: string) =>
      sourceType === 'Resource_Calendar'
        ? calendarEmailToNameMap.get(value) || value
        : sourceType === 'Shared_Drive'
        ? sharedDriveIdToNameMap.get(value) ?? value
        : value,
    [sourceType, calendarEmailToNameMap, sharedDriveIdToNameMap]
  );

  const leftListFooter = useMemo(
    () => (
      <>
        {sourceType === 'User' &&
        userDataHasNextPage &&
        !userDataIsFetchingNextPage ? (
          <Stack justifyContent="center" alignItems="center">
            <Button
              ref={ref}
              onClick={() => userDataFetchNextPage()}
              size="small"
            >
              {userDataHasNextPage ? 'Show more users' : 'No more users'}
            </Button>
          </Stack>
        ) : null}

        {sourceType === 'Group' &&
        groupDataHasNextPage &&
        !groupDataIsFetchingNextPage ? (
          <Stack justifyContent="center" alignItems="center">
            <Button
              ref={groupRef}
              onClick={() => groupDataFetchNextPage()}
              size="small"
            >
              {groupDataHasNextPage ? 'Show more groups' : 'No more groups'}
            </Button>
          </Stack>
        ) : null}

        {(userDataIsFetchingNextPage || groupDataIsFetchingNextPage) && (
          <ListItem
            style={{
              justifyContent: 'center',
              gap: 25,
              marginLeft: 12,
            }}
          >
            <Skeleton variant="rounded" width={20} height={20} />
            <Skeleton variant="rounded" width={'100%'} height={20} />
          </ListItem>
        )}

        {sourceType === 'Shared_Drive' &&
        sharedDriveDataHasNextPage &&
        !sharedDriveDataIsFetchingNextPage ? (
          <Stack justifyContent="center" alignItems="center">
            <Button
              ref={sharedDriveRef}
              onClick={() => sharedDriveDataFetchNextPage()}
              size="small"
            >
              {sharedDriveDataHasNextPage
                ? 'Show more shared drives'
                : 'No more shared drives'}
            </Button>
          </Stack>
        ) : null}
      </>
    ),
    [
      sourceType,
      userDataHasNextPage,
      userDataIsFetchingNextPage,
      userDataFetchNextPage,
      groupDataHasNextPage,
      groupDataIsFetchingNextPage,
      groupDataFetchNextPage,
      sharedDriveDataHasNextPage,
      sharedDriveDataIsFetchingNextPage,
      sharedDriveDataFetchNextPage,
      ref,
      groupRef,
      sharedDriveRef,
    ]
  );

  const customList = useCallback(
    (
      title: React.ReactNode,
      filteredItems: readonly string[],
      currentSearchValue: string,
      handleSearchInput: (value: string, isLeftList: boolean) => void,
      isLeftList: boolean
    ) => (
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          ...(!isLeftList &&
            errors &&
            !allSource && {
              border: (theme) => `1px solid ${theme.palette.error.main}`,
            }),
        }}
      >
        <Stack p={2} pb={0} flexDirection="row" gap={1} alignItems="center">
          <TextField
            label="Search"
            variant="outlined"
            value={currentSearchValue}
            onChange={(e) => handleSearchInput(e.target.value, isLeftList)}
            fullWidth
            disabled={isLeftList ? allSource : false}
          />
        </Stack>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={
                isLeftList
                  ? handleToggleAll(filteredItems)
                  : handleToggleAllRight
              }
              checked={
                isLeftList
                  ? numberOfChecked(filteredItems) === filteredItems.length &&
                    filteredItems.length !== 0
                  : numberOfChecked(rightFiltered) === rightFiltered.length &&
                    rightFiltered.length !== 0
              }
              indeterminate={
                isLeftList
                  ? numberOfChecked(filteredItems) !== filteredItems.length &&
                    numberOfChecked(filteredItems) !== 0
                  : numberOfChecked(rightFiltered) !== rightFiltered.length &&
                    numberOfChecked(rightFiltered) !== 0
              }
              disabled={
                isLeftList
                  ? filteredItems.length === 0 || allSource
                  : rightFiltered.length === 0
              }
              inputProps={{
                'aria-label': 'all items selected',
              }}
            />
          }
          action={
            showIncludeAll &&
            isLeftList && (
              <FormControlLabel
                control={
                  <Tooltip title="Add all targets, as well as any that are added in the future.">
                    <Checkbox
                      checked={allSource}
                      disabled={isMaxSelection}
                      onChange={(event) =>
                        handleIncludeAllChange(event.target.checked)
                      }
                    />
                  </Tooltip>
                }
                label="Add All"
              />
            )
          }
          title={title}
          subheader={`${
            isLeftList
              ? getSelectedCountForSourceType()
              : numberOfChecked(right)
          } selected`}
        />
        <Divider />

        {isLeftList && leftLoading ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              maxHeight: '30vh',
              padding: 0,
            }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <List
            sx={{
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              padding: 0,
              width: '100%',
            }}
            dense
            role="list"
          >
            {filteredItems.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                align="center"
                sx={{ mt: 2 }}
              >
                {isLeftList ? 'No results found' : 'None chosen'}
              </Typography>
            ) : (
              filteredItems.map((value: string) => {
                const labelId = `transfer-list-all-item-${value}-label`;

                if (
                  !isLeftList &&
                  sourceType === 'Resource_Calendar' &&
                  calendarsIsLoading
                ) {
                  return (
                    <ListItem key={value} dense>
                      <ListItemIcon>
                        <Skeleton
                          variant="rectangular"
                          width={20}
                          height={20}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                        }
                      />
                    </ListItem>
                  );
                }

                const primaryText = getItemLabel(value);

                return (
                  <ListItemButton
                    key={value}
                    role="listitem"
                    onClick={handleToggle(value)}
                    disabled={allSource}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={checked.includes(value)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={primaryText} />
                  </ListItemButton>
                );
              })
            )}
            {!isLeftList && errors && !allSource && (
              <Typography color="error" variant="caption" sx={{ ml: '14px' }}>
                {helperText}
              </Typography>
            )}

            {isLeftList && leftListFooter}
          </List>
        )}
      </Card>
    ),
    [
      checked,
      errors,
      handleToggle,
      handleToggleAll,
      handleToggleAllLeft,
      handleToggleAllRight,
      numberOfChecked,
      getSelectedCountForSourceType,
      allSource,
      helperText,
      calendarsIsLoading,
      sourceType,
      right,
      rightFiltered,
      isMaxSelection,
      leftListFooter,
      getItemLabel,
      showIncludeAll,
      leftLoading,
      handleIncludeAllChange,
    ]
  );

  const newTransferList = (
    <SharedTransferListNew
      sourceType={sourceType ?? ''}
      leftItems={leftFiltered}
      rightItems={rightFiltered}
      rightTotal={right.length}
      checked={checked}
      getLabel={getItemLabel}
      leftSearch={currentSearchValue}
      rightSearch={rightSearch}
      onSearch={handleSearchInput}
      onToggle={handleToggle}
      onToggleAllLeft={handleToggleAll(leftFiltered)}
      onToggleAllRight={handleToggleAllRight}
      leftSelectedCount={getSelectedCountForSourceType()}
      rightSelectedCount={numberOfChecked(right)}
      leftLoading={leftLoading}
      leftListFooter={leftListFooter}
      onAddSelected={handleCheckedRight}
      addSelectedDisabled={leftChecked.length === 0 || isMaxSelection}
      onRemove={handleCheckedLeft}
      removeDisabled={rightChecked.length === 0}
      showIncludeAll={showIncludeAll}
      allSource={allSource}
      includeAllDisabled={isMaxSelection}
      onIncludeAllChange={handleIncludeAllChange}
      errors={errors}
      helperText={helperText}
    />
  );

  return enterpriseBackendIndexingEnabled ? (
    <SharedBigQueryTransferList
      sources={sources}
      addSelection={addSelection}
      removeSelection={removeSelection}
      domains={domains}
      sourceType={sourceType}
      hasSelectAllOption={hasSelectAllOption}
      roles={roles}
      errors={errors}
      helperText={helperText}
      allSource={allSource}
      handleAddAllChecked={handleAddAllChecked}
      maxSelection={maxSelection}
      setSelectedSharedDrives={setSelectedSharedDrives}
      useBigQueryForUsers={useBigQueryForUsers}
      useBigQueryForGroups={useBigQueryForGroups}
      newUi={newUi}
    />
  ) : newUi ? (
    newTransferList
  ) : (
    <SharedTransferListLayout
      leftList={customList(
        'Choices',
        leftFiltered,
        currentSearchValue,
        handleSearchInput,
        true
      )}
      rightList={customList(
        'Chosen',
        rightFiltered,
        rightSearch,
        handleSearchInput,
        false
      )}
      onMoveRight={handleCheckedRight}
      onMoveLeft={handleCheckedLeft}
      moveRightDisabled={leftChecked.length === 0 || isMaxSelection}
      moveLeftDisabled={rightChecked.length === 0}
    />
  );
}
